import { Button, ImageMessageBox, Input, ModalV2 } from '@components'
import { FlagWrapper, useBusinessContext, useMobileEmbed } from '@context'
import { INocfoPermissions, IUsershipPermission, IUsershipPerson, updateUsership } from '@query'
import { NocfoPermissionGroups } from '@root/constants'
import { usePermissionGroups } from '@root/query/hooks'
import React, { useEffect, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import holviSrc from '@assets/holvi-logo.svg'
import toast from 'react-hot-toast'
import { Trans, useTranslation } from 'react-i18next'
import { FaSave } from 'react-icons/fa'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import { getBankIntegrationPageURL } from '@constants'
import { getMobileAwarePath } from '@root/utils/getMobileAwarePath'
import { matchPermissionPreset, PermissionPicker, PermissionPickerChange } from './PermissionPicker'
import { BusinessRoleSelector } from './BusinessRoleSelector'

interface Props {
  usership: IUsershipPerson
  isVisible: boolean
  onClose: () => void
}

interface INocfoPermissionWithId extends IUsershipPermission {
  id: string
}

export const UserEditForm: React.FC<Props> = ({ usership, isVisible, onClose }) => {
  const queryClient = useQueryClient()
  const { slug } = useBusinessContext()
  const [t] = useTranslation()
  const { isEmbedded } = useMobileEmbed()
  const { data: permissionGroups } = usePermissionGroups()

  const [selectedGroupId, setSelectedGroupId] = useState<string>(NocfoPermissionGroups.CUSTOM)

  const items: INocfoPermissionWithId[] = Object.entries(usership.permissions || {}).map(
    ([key, permission]) => ({
      ...permission,
      id: key
    })
  )

  const { handleSubmit, control, reset, setValue } = useForm<IUsershipPerson>({
    defaultValues: { ...usership, tags: usership.tags ?? [] }
  })

  useEffect(() => {
    if (!isVisible) return

    reset({ ...usership, tags: usership.tags ?? [] })

    const permissionIds = Object.entries(usership.permissions || {})
      .filter(([, permission]) => permission?.value)
      .map(([id]) => id)

    setSelectedGroupId(
      permissionGroups
        ? matchPermissionPreset(permissionIds, permissionGroups)
        : NocfoPermissionGroups.CUSTOM
    )
  }, [isVisible, usership, permissionGroups, reset])

  const _onClose = () => {
    onClose()
    reset({ ...usership, tags: usership.tags ?? [] })
  }

  const permissionsMap = useWatch({ control, name: 'permissions' })
  const selectedPermissionIds = Object.entries(permissionsMap || {})
    .filter(([, permission]) => permission?.value)
    .map(([id]) => id)

  const updateUsershipMutation = useMutation<unknown, unknown, Partial<IUsershipPerson>>({
    mutationFn: data => updateUsership({ slug, usershipId: usership?.id }, data),
    ...{
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [slug, 'people'] }),
          queryClient.invalidateQueries({ queryKey: ['user'] })
        ])
        toast.success(t('general.changesSaved'))
        onClose()
      },
      onError: error => {
        const { data, status } = (error as any) ?? {}
        if (status === 409) {
          toast.error(data?.error || t('general.error'))
        } else {
          toast.error(t('general.error'))
        }
      }
    }
  })

  const handlePickerChange = ({ groupId, permissionIds }: PermissionPickerChange) => {
    setSelectedGroupId(groupId)

    const updatedPermissions: Record<string, IUsershipPermission> = {}
    items.forEach(item => {
      updatedPermissions[item.id] = { ...item, value: permissionIds.includes(item.id) }
    })
    setValue('permissions', updatedPermissions as unknown as INocfoPermissions)
  }

  return (
    <ModalV2
      header={t('settings.people.edit')}
      isVisible={isVisible}
      handleOnClose={_onClose}
      width={800}
      height={900}
      footer={
        <ModalFooter>
          <Button type="button" onClick={() => _onClose()}>
            {t('general.cancel')}
          </Button>

          <Button
            intent="success"
            icon={<FaSave />}
            type="submit"
            form="user-edit-form"
            disabled={
              updateUsershipMutation.isPending ||
              (selectedGroupId === NocfoPermissionGroups.CUSTOM &&
                selectedPermissionIds.length === 0)
            }
            showSpinner={updateUsershipMutation.isPending}
          >
            {t('general.save')}
          </Button>
        </ModalFooter>
      }
    >
      <form
        id="user-edit-form"
        onSubmit={handleSubmit(data => {
          updateUsershipMutation.mutate(data)
        })}
      >
        <Input label={t('settings.people.email')} disabled={true} value={usership.email} />

        <FlagWrapper flag="workflows">
          <Controller
            name="tags"
            control={control}
            render={({ field }) => (
              <BusinessRoleSelector value={field.value ?? []} onChange={field.onChange} />
            )}
          />
        </FlagWrapper>

        <StyledPermissionsHeader>
          <h4>{t('settings.people.permissions')}</h4>
          <PermissionsHint>{t('settings.people.permissionExplanation')}</PermissionsHint>
        </StyledPermissionsHeader>

        {usership.managed_holvi_accounts.length > 0 && (
          <>
            <ImageMessageBox imgSrc={holviSrc} size="sm">
              <Trans
                i18nKey="settings.people.holviPermissions"
                components={[
                  <a
                    key="link"
                    href={getMobileAwarePath(
                      getBankIntegrationPageURL(slug, 'holvi-edit'),
                      isEmbedded
                    )}
                  />
                ]}
              />
            </ImageMessageBox>
          </>
        )}

        <StyledPickerWrapper>
          <PermissionPicker
            groups={permissionGroups ?? []}
            permissions={items}
            selectedGroupId={selectedGroupId}
            selectedPermissionIds={selectedPermissionIds}
            onChange={handlePickerChange}
          />
        </StyledPickerWrapper>
      </form>
    </ModalV2>
  )
}

const StyledPickerWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md}rem;
`

const StyledPermissionsHeader = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md}rem;

  h4 {
    margin: 0 0 ${({ theme }) => theme.spacing.xs}rem 0;
  }
`

const PermissionsHint = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  line-height: 1.5;
`

const ModalFooter = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
`
